import React from 'react';
import { Field, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { useLocaleUtils } from 'common/hooks';
import { useIsMinQuantityFieldEnabled } from 'modules/menu/hooks/useIsMinQuantityFieldEnabled';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { FormRowRenderer } from '../FormRowRenderer';
import {
  FormRowWrapper,
  PurchaseQuantityLabel,
  PurchaseQuantityWrapper,
  StyledInputContainer,
} from '../OfferDetailsFormFields.styles';
import { AvailabilityDetails, FormValues } from '../types';

export const PerPurchase = ({ sidePanelMode, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();
  const isMinQuantityFieldEnabled = useIsMinQuantityFieldEnabled();
  const { getLocalizedUnit } = useLocaleUtils();

  const { values } = useFormState<FormValues>();

  const previewMinQuantityComponent = !offer?.min_quantity_per_purchase
    ? '-'
    : `${offer.min_quantity_per_purchase}${
        offer.sell_by_weight_config?.input_type === 'grams' ? getLocalizedUnit('gram') : '-'
      }`;

  const previewMaxQuantityComponent = !offer?.max_quantity_per_purchase
    ? '-'
    : `${offer.max_quantity_per_purchase}${
        offer.sell_by_weight_config?.input_type === 'grams' ? getLocalizedUnit('gram') : '-'
      }`;

  const editPerPurchaseComponent = (
    <FormRowWrapper>
      {isMinQuantityFieldEnabled && (
        <PurchaseQuantityWrapper>
          <PurchaseQuantityLabel>
            {intl.formatMessage({ id: 'menu-item.min-quantity-per-purchase' })}
          </PurchaseQuantityLabel>
          <Field<AvailabilityDetails['minQuantityPerPurchase']> name="minQuantityPerPurchase">
            {field => (
              <RFFFieldErrorWrapper field={field} showErrorImmediately>
                {input => (
                  <StyledInputContainer>
                    <InputNumber
                      {...input}
                      suffixText={
                        values.sellByWeightInputType === 'grams' && values.weightType === 'weighted-items'
                          ? getLocalizedUnit('gram')
                          : ''
                      }
                    />
                  </StyledInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </PurchaseQuantityWrapper>
      )}
      <PurchaseQuantityWrapper>
        <PurchaseQuantityLabel>
          {intl.formatMessage({ id: 'menu-item.max-quantity-per-purchase' })}
        </PurchaseQuantityLabel>
        <Field<AvailabilityDetails['maxQuantityPerPurchase']> name="maxQuantityPerPurchase">
          {field => (
            <RFFFieldErrorWrapper field={field} showErrorImmediately>
              {input => (
                <StyledInputContainer>
                  <InputNumber
                    {...input}
                    suffixText={
                      values.sellByWeightInputType === 'grams' && values.weightType === 'weighted-items'
                        ? getLocalizedUnit('gram')
                        : ''
                    }
                  />
                </StyledInputContainer>
              )}
            </RFFFieldErrorWrapper>
          )}
        </Field>
      </PurchaseQuantityWrapper>
    </FormRowWrapper>
  );

  if (sidePanelMode !== 'preview') {
    return (
      <FormRowRenderer
        hasContentEditPermission={menuPermissions.contentEdit}
        label={intl.formatMessage({ id: 'menu-item.per-purchase' })}
        shouldDisplayCreateTag={!values.maxQuantityPerPurchase}
        previewComponent={null}
        editComponent={editPerPurchaseComponent}
        sidePanelMode={sidePanelMode}
        offer={offer}
      />
    );
  }

  return (
    <>
      {isMinQuantityFieldEnabled && (
        <FormRowRenderer
          hasContentEditPermission={menuPermissions.contentEdit}
          label={intl.formatMessage({ id: 'menu-item.min-quantity-per-purchase' })}
          previewComponent={previewMinQuantityComponent}
          editComponent={null}
          sidePanelMode={sidePanelMode}
          offer={offer}
        />
      )}

      <FormRowRenderer
        hasContentEditPermission={menuPermissions.contentEdit}
        label={intl.formatMessage({ id: 'menu-item.max-quantity-per-purchase' })}
        previewComponent={previewMaxQuantityComponent}
        editComponent={null}
        sidePanelMode={sidePanelMode}
        offer={offer}
      />
    </>
  );
};
